/**
 * MobileOrgCard Component
 *
 * Card-based display for organization data on mobile devices.
 * Replaces the DataTable view for better touch interaction.
 */

import React from 'react';
import { Box, Typography, Chip, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MemoryIcon from '@mui/icons-material/Memory';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system/tokens';
import { Selection } from '../orgNavTypes';
import type { FilterInput } from '../OrgNav';

// Types for different entity responses
interface CompanyData {
    companyId: number;
    companyName: string;
    inactive: boolean;
    numResellers: number;
    numLocations: number;
}

interface ResellerData {
    resellerId: number;
    resellerName: string;
    companyName: string;
    companyId: number;
    inactive: boolean;
    numLocations: number;
}

interface LocationData {
    locationId: number;
    locationName: string;
    resellerName: string | null;
    resellerId: number | null;
    companyName: string;
    companyId: number;
    inactive: boolean;
    numSystems: number;
}

type OrgData = CompanyData | ResellerData | LocationData;

interface MobileOrgCardProps {
    data: OrgData;
    tab: Selection.Company | Selection.Reseller | Selection.Location;
    onFilterChange?: (filters: FilterInput, newTab: Selection) => void;
}

const CardContainer: React.FC<{ children: React.ReactNode; to: string }> = ({ children, to }) => (
    <Box
        component={RouterLink}
        to={to}
        sx={{
            display: 'block',
            textDecoration: 'none',
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
            boxShadow: shadows.card,
            transition: 'all 150ms ease',
            '&:hover': {
                boxShadow: shadows.elevated,
                borderColor: colors.primary[200],
            },
            '&:active': {
                transform: 'scale(0.99)',
            },
        }}>
        {children}
    </Box>
);

const StatItem: React.FC<{
    label: string;
    value: number;
    onClick?: (e: React.MouseEvent) => void;
}> = ({ label, value, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 1.5,
            py: 1,
            borderRadius: `${radii.md}px`,
            backgroundColor: colors.neutral[50],
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 150ms ease',
            ...(onClick && {
                '&:hover': {
                    backgroundColor: colors.primary[50],
                },
            }),
        }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.lg,
                fontWeight: typographyTokens.fontWeight.semibold,
                color: onClick ? colors.primary[600] : colors.neutral[800],
            }}>
            {value}
        </Typography>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.xs,
                color: colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}>
            {label}
        </Typography>
    </Box>
);

const CompanyCard: React.FC<{
    data: CompanyData;
    onFilterChange?: (filters: FilterInput, newTab: Selection) => void;
}> = ({ data, onFilterChange }) => (
    <CardContainer to={`/manage/organizations?type=${Selection.Company}&id=${data.companyId}`}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: `${radii.md}px`,
                    backgroundColor: `${colors.primary[500]}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                <BusinessIcon sx={{ color: colors.primary[500], fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.md,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            color: colors.neutral[900],
                            lineHeight: 1.3,
                        }}>
                        {data.companyName}
                    </Typography>
                    <Chip
                        size='small'
                        label={data.inactive ? 'Archived' : 'Active'}
                        sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            backgroundColor: data.inactive ? colors.warning.light : colors.success.light,
                            color: data.inactive ? colors.warning.dark : colors.success.dark,
                            fontWeight: 500,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontSize: typographyTokens.fontSize.xs,
                        color: colors.neutral[500],
                        mt: 0.25,
                    }}>
                    Company #{data.companyId}
                </Typography>
            </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
            <StatItem
                label='Resellers'
                value={data.numResellers}
                onClick={
                    onFilterChange
                        ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange(
                                  { companies: { [data.companyId]: true }, resellers: {}, locations: {} },
                                  Selection.Reseller
                              );
                          }
                        : undefined
                }
            />
            <StatItem
                label='Locations'
                value={data.numLocations}
                onClick={
                    onFilterChange
                        ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange(
                                  { companies: { [data.companyId]: true }, resellers: {}, locations: {} },
                                  Selection.Location
                              );
                          }
                        : undefined
                }
            />
        </Box>
    </CardContainer>
);

const ResellerCard: React.FC<{
    data: ResellerData;
    onFilterChange?: (filters: FilterInput, newTab: Selection) => void;
}> = ({ data, onFilterChange }) => (
    <CardContainer to={`/manage/organizations?type=${Selection.Reseller}&id=${data.resellerId}`}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: `${radii.md}px`,
                    backgroundColor: `${colors.warning.main}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                <StorefrontIcon sx={{ color: colors.warning.main, fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.md,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            color: colors.neutral[900],
                            lineHeight: 1.3,
                        }}>
                        {data.resellerName}
                    </Typography>
                    <Chip
                        size='small'
                        label={data.inactive ? 'Archived' : 'Active'}
                        sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            backgroundColor: data.inactive ? colors.warning.light : colors.success.light,
                            color: data.inactive ? colors.warning.dark : colors.success.dark,
                            fontWeight: 500,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontSize: typographyTokens.fontSize.xs,
                        color: colors.neutral[500],
                        mt: 0.25,
                    }}>
                    {data.companyName}
                </Typography>
            </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
            <StatItem
                label='Locations'
                value={data.numLocations}
                onClick={
                    onFilterChange
                        ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange(
                                  {
                                      companies: { [data.companyId]: true },
                                      resellers: { [data.resellerId]: true },
                                      locations: {},
                                  },
                                  Selection.Location
                              );
                          }
                        : undefined
                }
            />
        </Box>
    </CardContainer>
);

const LocationCard: React.FC<{
    data: LocationData;
    onFilterChange?: (filters: FilterInput, newTab: Selection) => void;
}> = ({ data, onFilterChange }) => (
    <CardContainer to={`/manage/organizations?type=${Selection.Location}&id=${data.locationId}`}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: `${radii.md}px`,
                    backgroundColor: `${colors.success.main}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                <LocationOnIcon sx={{ color: colors.success.main, fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.md,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            color: colors.neutral[900],
                            lineHeight: 1.3,
                        }}>
                        {data.locationName}
                    </Typography>
                    <Chip
                        size='small'
                        label={data.inactive ? 'Archived' : 'Active'}
                        sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            backgroundColor: data.inactive ? colors.warning.light : colors.success.light,
                            color: data.inactive ? colors.warning.dark : colors.success.dark,
                            fontWeight: 500,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontSize: typographyTokens.fontSize.xs,
                        color: colors.neutral[500],
                        mt: 0.25,
                    }}>
                    {data.companyName}
                    {data.resellerName && ` • ${data.resellerName}`}
                </Typography>
            </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
            <StatItem
                label='Systems'
                value={data.numSystems}
                onClick={
                    onFilterChange
                        ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange(
                                  {
                                      companies: { [data.companyId]: true },
                                      resellers: data.resellerId ? { [data.resellerId]: true } : {},
                                      locations: { [data.locationId]: true },
                                  },
                                  Selection.System
                              );
                          }
                        : undefined
                }
            />
        </Box>
    </CardContainer>
);

export const MobileOrgCard: React.FC<MobileOrgCardProps> = ({ data, tab, onFilterChange }) => {
    if (tab === Selection.Company) {
        return <CompanyCard data={data as CompanyData} onFilterChange={onFilterChange} />;
    }

    if (tab === Selection.Reseller) {
        return <ResellerCard data={data as ResellerData} onFilterChange={onFilterChange} />;
    }

    return <LocationCard data={data as LocationData} onFilterChange={onFilterChange} />;
};

// Loading skeleton for mobile cards
export const MobileOrgCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='70%' height={24} />
                <Skeleton variant='text' width='40%' height={16} />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant='rounded' width={80} height={60} />
            <Skeleton variant='rounded' width={80} height={60} />
        </Box>
    </Box>
);

export default MobileOrgCard;

